import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaHome, FaCalculator, FaBalanceScale, FaChartLine, FaBuilding, FaUsers } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const Immobilier = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaCalculator />,
            title: "Comptabilité, Fiscalité et Paie",
            description: "Gestion comptable, établissement des comptes annuels, optimisation fiscale et gestion sociale adaptées aux professionnels de l'immobilier.",
            color: "#0d6efd"
        },
        {
            icon: <FaBuilding />,
            title: "Gestion Spécifique aux Métiers de l'Immobilier",
            description: "Services adaptés aux syndics de copropriété et à la promotion immobilière avec prise en compte des spécificités du secteur.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Conseil et Accompagnement Stratégique",
            description: "Accompagnement à la création, reprise et développement de votre agence ou société immobilière.",
            color: "#dc3545"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Tenue et révision comptable",
                description: "Gestion rigoureuse de votre comptabilité, adaptée à votre structure (agence, syndic, promotion, etc.) et à votre activité. Prise en compte des spécificités (comptes séquestres, comptes de copropriété, etc.)."
            },
            {
                title: "Établissement des comptes annuels",
                description: "Bilan, compte de résultat, annexe, et production de liasses fiscales."
            },
            {
                title: "Optimisation fiscale",
                description: "Recherche des meilleures options pour réduire votre charge fiscale (régimes spécifiques, défiscalisation immobilière, etc.)."
            },
            {
                title: "Déclarations fiscales et sociales",
                description: "Prise en charge de l'ensemble de vos déclarations."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail, accompagnement en cas de contrôle la CNSS. Maîtrise des conventions collectives applicables."
            },
            {
                title: "Assistance en cas de contrôle fiscal ou social",
                description: "Accompagnement complet et défense de vos intérêts."
            },
            {
                title: "Gestion des SCI",
                description: "Tenue de la comptabilité, déclarations fiscales, assistance aux assemblées générales."
            }
        ],
        gestion: [
            {
                title: "Syndics de copropriété",
                description: "Tenue de la comptabilité des copropriétés (comptabilité d'engagement), établissement des budgets prévisionnels, des appels de fonds, et des décomptes de charges."
            },
            {
                title: "Gestion des comptes bancaires séparés",
                description: "Suivi et gestion conforme à la réglementation."
            },
            {
                title: "Préparation et assistance aux assemblées générales",
                description: "Aide à la préparation des documents financiers et présence lors des assemblées si nécessaire."
            },
            {
                title: "Suivi des contentieux",
                description: "Accompagnement dans la gestion des impayés et des litiges."
            },
            {
                title: "Promotion immobilière",
                description: "Comptabilité de programme, suivi des budgets et des coûts de construction."
            },
            {
                title: "Gestion de la TVA",
                description: "Gestion de la TVA sur les opérations de construction et optimisation fiscale."
            }
        ],
        conseil: [
            {
                title: "Accompagnement à la création et à la reprise d'agence/société",
                description: "Étude de marché, business plan, choix de la forme juridique, recherche de financements, formalités administratives."
            },
            {
                title: "Conseil en investissement immobilier",
                description: "Analyse de la rentabilité, optimisation fiscale, recherche de financements."
            },
            {
                title: "Évaluation d'agence immobilière ou de portefeuille de gestion",
                description: "Dans le cadre d'une cession, d'une acquisition ou d'une transmission."
            },
            {
                title: "Stratégie de développement",
                description: "Croissance organique, croissance externe (acquisition d'agences ou de portefeuilles), diversification."
            },
            {
                title: "Conseil en gestion des ressources humaines",
                description: "Recrutement, fidélisation, optimisation des coûts salariaux."
            },
            {
                title: "Accompagnement à la transformation digitale",
                description: "Mise en place d'outils numériques de gestion (CRM, logiciel de transaction, logiciel de gestion locative, etc.), dématérialisation."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour les Professionnels de l'Immobilier</h1>
                            <p className="fs-5 mb-4">Agences, Administrateurs de Biens, Promoteurs : Optimisez Votre Gestion et Votre Rentabilité</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les enjeux spécifiques du secteur immobilier
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Le secteur immobilier est en constante évolution : réglementation complexe (loi Hoguet, loi ALUR, etc.), 
                                forte concurrence, exigences de transparence... CINEF-EC vous apporte une expertise comptable, 
                                juridique et fiscale pointue, ainsi qu'un accompagnement personnalisé pour vous aider à gérer votre activité 
                                avec succès et à développer votre entreprise.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Une expertise dédiée aux professionnels de l'immobilier</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des professionnels de l'immobilier.
                                Nous connaissons parfaitement les spécificités comptables, fiscales et juridiques liées à ce secteur.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans vos obligations légales et réglementaires
                                tout en vous aidant à développer votre activité et à optimiser votre rentabilité.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaHome className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Agences immobilières</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaBuilding className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Syndics</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaUsers className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Promoteurs</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Fiscalité et Paie</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Gestion Spécifique aux Métiers de l'Immobilier</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.gestion.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`gestion-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Vous souhaitez optimiser la gestion de votre agence/société immobilière ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée. 
                                    Nous analyserons ensemble vos besoins et vous proposerons des solutions adaptées 
                                    à votre activité pour vous permettre de vous concentrer sur votre développement.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/real-estate-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default Immobilier;